import Alert from '@risk-smart/themed-cloudscape-components/alert';
import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import {
  arrayToCsv,
  downloadBlob,
} from '@risksmart-app/components/src/file/fileUtils';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { ReportingDataInput } from '@risksmart-app/shared/reporting/api/schema';
import type { GetReportingDataQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useModulesStore } from 'src/context/moduleContext';
import PageLayout from 'src/layouts/PageLayout';
import { useCustomAttributeLookup } from 'src/pages/dashboards/custom-data-source-widget/useCustomAttributeLookup';
import { Permission } from 'src/rbac/Permission';

import {
  useGetAllFormsCustomisation,
  useGetCustomDatasourceById,
  useGetReportingData,
  useLazyGetReportingData,
} from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { emptyFilterQuery } from '@/utils/collectionUtils';
import { handleError } from '@/utils/errorUtils';
import { useFiltersFromUrlHash } from '@/utils/table/hooks/useFiltersFromUrlHash';
import { editCustomDatasourceUrl } from '@/utils/urls';

import type { TypedCustomDatasource } from '../types';
import { CustomDatasourceModel } from '../update/customDatasourceModel';
import CustomDatasourcePropertyFilter from '../update/CustomDatasourcePropertyFilter';
import CustomDatasourceTable from '../update/CustomDatasourceTable';
import { getFieldUniqueId } from '../update/fieldValue';
import { DEFAULT_PAGE_SIZE } from '../update/tablePreferences';
import { combinedFilters } from './combineFilters';
import { useCustomDatasourceExporterMapper } from './customDatasourceExporter';

const Page: FC = () => {
  const { addNotification } = useNotifications();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();
  const [pageIndex, setPageIndex] = useState(0);
  const [preferences, setPreferences] =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useState<CollectionPreferencesProps.Preferences<any>>();
  const pageSize = preferences?.pageSize ?? DEFAULT_PAGE_SIZE;
  const customDataSourceId = useGetGuidParam('customDatasourceId');
  const { data: customDatasourceData, loading } = useGetCustomDatasourceById({
    queryArgs: { id: customDataSourceId },
  });
  const customDatasource: null | TypedCustomDatasource | undefined =
    customDatasourceData?.custom_datasource_by_pk;
  if (!loading && !customDatasource) {
    throw new PageNotFound(
      `Custom datasource with id ${customDataSourceId} not found`
    );
  }

  const filterFields = useMemo(() => {
    if (!customDatasource?.Fields) {
      return {};
    }

    return Object.fromEntries(
      customDatasource.Fields.map((f) => [getFieldUniqueId(f), { header: '' }])
    );
  }, [customDatasource?.Fields]);

  const { propertyFilter, setPropertyFilter, hash } = useFiltersFromUrlHash({
    fields: filterFields,
  });

  const { t } = useTranslation(['common'], { keyPrefix: 'customDatasources' });
  const { t: rt } = useTranslation(['common']);
  const { data: formCustomisationData } = useGetAllFormsCustomisation({
    queryArgs: {},
  });
  const { customAttributeSchemaLookup } = useCustomAttributeLookup();

  const formatForExport = useCustomDatasourceExporterMapper({
    customAttributeSchemaLookup,
    customDatasource,
    formFieldConfigurations:
      formCustomisationData?.form_field_configuration ?? null,
  });
  const getReportingInput = useCallback(
    (
      customDatasource: null | TypedCustomDatasource | undefined,
      paging: { offset: number; limit: number }
    ): ReportingDataInput | undefined => {
      if (!customDatasource) {
        return undefined;
      }

      return {
        filters: combinedFilters(customDatasource, propertyFilter),
        dataSources: customDatasource.Datasources,
        fields: customDatasource.Fields ?? [],
        ...paging,
      };
    },
    [propertyFilter]
  );

  // TODO: potentially replace this with a new api that acceptances a customDatasourceId
  // this will avoid the need to call useGetCustomDatasourceByIdQuery first, and could offer bet security
  // if we want to look down the end point that supports any kind of query
  const {
    data: reportData,
    loading: reportDataLoading,
    error: reportDataError,
  } = useGetReportingData({
    queryArgs: getReportingInput(customDatasource, {
      offset: pageIndex * pageSize,
      limit: pageSize,
    }),
    shouldSkip: !customDatasource,
  });
  const [getReportData, getReportDataResult] = useLazyGetReportingData();

  const exportToCsv = useCallback(async () => {
    // Lambda sync invocation caps response payloads at 6 MB, so we page the
    // export instead of requesting all rows in one call.
    const CHUNK_SIZE = 5000;
    const EXPORT_ROW_CAP = 100000;
    const allRows: NonNullable<GetReportingDataQuery['reportingData']> = [];

    for (let offset = 0; offset < EXPORT_ROW_CAP; offset += CHUNK_SIZE) {
      const limit = Math.min(CHUNK_SIZE, EXPORT_ROW_CAP - offset);
      const input = getReportingInput(customDatasource, { offset, limit });
      if (!input) {
        return;
      }
      const results = await getReportData(input);
      if (results.error) {
        handleError(results.error, undefined, 'customDatasource.exportToCsv');
        addNotification({
          type: 'error',
          content: rt('export.export_failed_message'),
        });

        return;
      }

      const rows = results.data?.reportingData ?? [];
      allRows.push(...rows);
      if (rows.length < limit) {
        break;
      }
    }

    const formatted = formatForExport(allRows);

    const csv = arrayToCsv(formatted);
    const blob = new Blob(['﻿', csv], {
      type: 'text/csv;charset=utf-8',
    });

    downloadBlob(`export.csv`, blob);
  }, [
    getReportData,
    getReportingInput,
    customDatasource,
    addNotification,
    rt,
    formatForExport,
  ]);

  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const modules = useModulesStore((s) => s.modules);
  const customDatasourceModel =
    customDatasource && customAttributeSchemaLookup
      ? CustomDatasourceModel(
          customDatasource,
          customAttributeSchemaLookup,
          formCustomisationData?.form_field_configuration ?? null,
          formSectionsEnabled,
          modules
        )
      : null;

  // Object-level toolbar (CUS-172) — flag-gated. This detail surface is a
  // read-only reporting-data preview (no tabs, no editable form), so the toolbar
  // relocates the two existing header actions: CSV export (Download group) and
  // Edit (navigate to the definition editor, gated on update:custom_datasource).
  // No save disc — there is no object form on this page.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: customDatasource?.Title ?? t('create_title') },
      actions: customDatasource
        ? {
            overflow: [
              {
                id: 'edit',
                label: rt('edit'),
                actions: [
                  {
                    id: 'edit-datasource',
                    label: t('edit_button'),
                    iconName: 'pencil-01',
                    permission: 'update:custom_datasource',
                    onClick: () => {
                      if (customDataSourceId) {
                        navigate(
                          editCustomDatasourceUrl(customDataSourceId) + hash
                        );
                      }
                    },
                  },
                ],
              },
            ],
            export: [
              {
                id: 'export',
                actions: [
                  {
                    id: 'csv',
                    label: rt('export.export'),
                    iconName: 'download-01',
                    onClick: exportToCsv,
                    disabled: getReportDataResult.loading || loading,
                  },
                ],
              },
            ],
          }
        : undefined,
    }),
    [
      customDatasource,
      customDataSourceId,
      hash,
      navigate,
      exportToCsv,
      getReportDataResult.loading,
      loading,
      t,
      rt,
    ]
  );

  return (
    <PageLayout
      title={customDatasource?.Title ?? t('create_title')}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        customDatasource && (
          <SpaceBetween direction={'horizontal'} size={'xxs'}>
            <Button
              iconName={'download'}
              disabled={getReportDataResult.loading || loading}
              onClick={exportToCsv}
            >
              {rt('export.export')}
            </Button>
            <Permission permission={'update:custom_datasource'}>
              <Button
                variant={'normal'}
                formAction={'none'}
                href={
                  customDataSourceId &&
                  editCustomDatasourceUrl(customDataSourceId) + hash
                }
              >
                {t('edit_button')}
              </Button>
            </Permission>
          </SpaceBetween>
        )
      }
    >
      {reportDataError && !reportDataLoading && (
        <Alert type={'error'}>{t('data_request_failure_message')}</Alert>
      )}
      {customDatasourceModel &&
        customDatasourceModel.deletedFields.length > 0 && (
          <Alert type={'warning'}>
            {t('deleted_fields_notice', {
              fields: customDatasourceModel.deletedFields
                .map((f) => f.label ?? f.defaultLabel)
                .join(', '),
            })}
          </Alert>
        )}
      {customDatasourceModel &&
        customAttributeSchemaLookup &&
        customDatasource && (
          <CustomDatasourceTable
            preferences={preferences}
            onSetPreferences={(updated) => {
              // Page size drives the server-side limit/offset, so changing it
              // invalidates the current page index. Compare effective sizes so
              // explicitly picking the already-active default isn't treated as a
              // change (mirrors the `pageSize` derivation above).
              const nextPageSize = updated.pageSize ?? DEFAULT_PAGE_SIZE;
              if (nextPageSize !== pageSize) {
                setPageIndex(0);
              }
              setPreferences(updated);
            }}
            columnsAlwaysVisible={false}
            filter={
              customDatasource && (
                <CustomDatasourcePropertyFilter
                  query={propertyFilter ?? emptyFilterQuery}
                  onChange={(e) => {
                    setPageIndex(0);
                    setPropertyFilter(e);
                  }}
                  allFields={customDatasourceModel.fields}
                  datasources={customDatasource.Datasources}
                />
              )
            }
            formFieldConfigurations={
              formCustomisationData?.form_field_configuration ?? null
            }
            customAttributeSchemaLookup={customAttributeSchemaLookup}
            loading={reportDataLoading}
            pageSize={pageSize}
            currentPageIndex={pageIndex}
            onPageChangeClick={({ requestedPageIndex }) => {
              setPageIndex(requestedPageIndex);
            }}
            customDatasource={customDatasource}
            items={reportData?.reportingData ?? []}
          />
        )}
    </PageLayout>
  );
};

export default Page;
